import * as React from 'react'
import {SafeTranslation} from '@freckle/student-materials/src/helpers/translate'
import type {CancelablePromise} from '@freckle/cancelable-promise'
import {makeCancelable} from '@freckle/cancelable-promise'
import {faSpinner, faStop, faVolumeUp} from '@fortawesome/free-solid-svg-icons'
import {type SyntheticEvent} from 'react'

import {createHTMLAudio} from './../../helpers/tts'
import {ButtonCircular, type StyleT, type IconT, type SizeT} from './../button-circular'

export type Props = {
  audioFilePath: string | undefined | null
  style?: StyleT
  customIcon?: IconT
  disabled?: boolean
  addClass?: string
  size?: SizeT
  tabIndex?: number
  inline?: boolean
  dataTest?: string
  onAudioFailure?: (err: string) => void
  onAudioComplete?: () => void
  onAudioPlay?: () => void
  onAudioReset?: () => void
  toggleAudioPlay?: boolean
  ariaLabel?: string
  role?: string
  tryAutoPlay?: boolean
}

type State = {
  isLoading: boolean
  isPlaying: boolean
  audioNode: HTMLMediaElement | undefined | null
  hasAudio: boolean
}

function appendParamToRemedyCorsBug(path: string): string {
  return path.includes('?') ? `${path}&via=xmlHttpRequest` : `${path}?via=xmlHttpRequest`
}

export class BaseReadAloud extends React.PureComponent<Props, State> {
  existingResourcePromise: CancelablePromise<Response> | undefined | null

  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      audioNode: null,
      hasAudio: false,
      isPlaying: false
    }

    this.removeAndRecreateAudio = this.removeAndRecreateAudio.bind(this)
    this.onClick = this.onClick.bind(this)
  }

  async testExistingResource(audioFilePath?: string | null): Promise<boolean> {
    if (audioFilePath === null || audioFilePath === undefined) {
      this.setState({hasAudio: false})
      return false
    } else {
      /**
       * Addresses Chrome CORS bug.  More info in asset-path-helper file
       *
       * Root cause: https://bugs.chromium.org/p/chromium/issues/detail?id=260239
       */
      const audioPathWithAppendedParam = appendParamToRemedyCorsBug(audioFilePath)

      this.existingResourcePromise = makeCancelable(
        fetch(audioPathWithAppendedParam, {
          method: 'HEAD',
          mode: 'cors'
        })
      )

      try {
        await this.existingResourcePromise.promise
        this.setState({hasAudio: true})
        return true
      } catch (e) {
        // @ts-ignore: TODO Address as debt
        if (!e.isCanceled) {
          this.setState({hasAudio: false})
        }
        return false
      }
    }
  }

  async componentDidMount() {
    const {audioFilePath, tryAutoPlay} = this.props
    const hasAudio = await this.testExistingResource(audioFilePath)
    if (hasAudio && tryAutoPlay) {
      this.read()
    }
  }

  componentWillUnmount() {
    const {audioNode} = this.state
    const {onAudioReset} = this.props
    if (audioNode) {
      audioNode.pause()
      if (onAudioReset) {
        onAudioReset()
      }
    }
    if (this.existingResourcePromise) {
      this.existingResourcePromise.cancel()
    }
  }

  async componentDidUpdate(prevProps: Props) {
    const {audioFilePath, disabled, onAudioReset, toggleAudioPlay, tryAutoPlay} = this.props
    // we only want to pause/reset the audio
    // if the file path changed
    if (prevProps.audioFilePath !== audioFilePath) {
      if (this.state.audioNode) {
        this.state.audioNode.pause()
        if (onAudioReset) {
          onAudioReset()
        }
      }
      if (disabled !== true) {
        const hasAudio = await this.testExistingResource(audioFilePath)
        if (hasAudio && tryAutoPlay) {
          this.read()
        }
      }
    }
    if (prevProps.toggleAudioPlay !== toggleAudioPlay) {
      this.removeAndRecreateAudio()
    }
  }

  removeAndRecreateAudio() {
    const {onAudioReset} = this.props

    // When user clicks and audioNode is loading or done, we stop it
    // and remove the audio node to avoid having it read when loading is done
    if (this.state.audioNode) {
      this.state.audioNode.pause()
      if (onAudioReset) {
        onAudioReset()
      }
      this.setState({
        audioNode: null,
        isLoading: false,
        hasAudio: true
      })
    }
    // This will replace current audioNode
    this.read()
  }

  onClick(e: SyntheticEvent<Element, Event>) {
    const {onAudioReset} = this.props
    const {audioNode} = this.state

    e.preventDefault()
    e.stopPropagation()
    if (audioNode?.paused ?? true) {
      this.removeAndRecreateAudio()
    } else {
      audioNode?.pause()
      onAudioReset?.()
    }
  }

  read() {
    const {audioFilePath, onAudioFailure, onAudioComplete, onAudioPlay, tryAutoPlay} = this.props

    if (audioFilePath === null || audioFilePath === undefined) {
      this.setState({
        hasAudio: false
      })
    } else {
      const audioNode = createHTMLAudio(audioFilePath, onAudioFailure, onAudioComplete)
      if (audioNode) {
        audioNode.load()
        audioNode.addEventListener('canplaythrough', {
          handleEvent: () => {
            // If terminateTextToSpeech has been called before the end of loading
            this.setState({isLoading: false})
            if (this.state.audioNode === audioNode) {
              if (tryAutoPlay) {
                // trying to auto play might cause permission errors
                audioNode.play().catch(_e => {})
              } else {
                audioNode.play()
              }
              if (onAudioPlay !== null && onAudioPlay !== undefined) {
                onAudioPlay()
              }
            }
          }
        })
        audioNode.addEventListener('play', () => {
          if (this.state.audioNode === audioNode) {
            this.setState({isPlaying: true})
          }
        })
        audioNode.addEventListener('pause', () => {
          if (this.state.audioNode === audioNode) {
            this.setState({isPlaying: false})
          }
        })
        this.setState({
          isLoading: true,
          hasAudio: true,
          audioNode
        })
      }
    }
  }

  render(): React.ReactNode {
    const {isLoading, hasAudio, isPlaying} = this.state
    const {
      addClass,
      inline,
      customIcon,
      disabled,
      dataTest,
      style = 'tertiary',
      size,
      tabIndex,
      ariaLabel,
      role
    } = this.props
    const icon: IconT = isLoading
      ? {tag: 'icon', icon: faSpinner, spin: true}
      : customIcon !== null && customIcon !== undefined
        ? customIcon
        : getToggleIcon(isPlaying)

    return hasAudio ? (
      <SafeTranslation ns="common">
        {t => (
          <ButtonCircular
            icon={icon}
            title={{tag: 'with-translation', str: 'AUDIO_READ_ALOUD_BUTTON_TITLE', translatorFn: t}}
            data-test={dataTest}
            type="button"
            addClass={addClass}
            inline={inline}
            onClick={this.onClick}
            isDisabled={disabled}
            style={style}
            size={size}
            iconSize="sm"
            tabIndex={tabIndex}
            aria-label={ariaLabel}
            role={role}
            isToggle
          />
        )}
      </SafeTranslation>
    ) : null
  }
}

function getToggleIcon(isPlaying: boolean | undefined): IconT {
  return isPlaying ? {tag: 'icon', icon: faStop} : {tag: 'icon', icon: faVolumeUp}
}
